import { useEffect, useState, type RefObject } from 'react';

import { ConsentGuard } from '@hivebrite/design-system/Embed';

import { type Replacement } from 'hb-react/shared/components/HtmlContent/replacementEngine';
import { extractConsentGuardProps } from 'hb-react/shared/components/HtmlContent/utils/extractConsentGuardProps';
import { parseCssString } from 'hb-react/shared/components/HtmlContent/utils/parseCssString';
import { ImageWithPreview } from 'hb-react/shared/components/ImageWithPreview';

interface Portal {
  element: JSX.Element;
  key: string;
  target: Element;
}

interface UseReplacementRendererProps {
  containerRef: RefObject<HTMLElement>;
  replacements: Array<Replacement> | undefined;
}

/**
 * Custom hook that renders replacement elements as React portals
 *
 * @param replacements - Array of processed replacements from the replacement engine
 * @param containerRef - Reference to the container element where replacements will be rendered
 * @returns Array of portal objects for rendering
 */
export const useReplacementRenderer = ({
  replacements,
  containerRef,
}: UseReplacementRendererProps): Array<Portal> => {
  const [portals, setPortals] = useState<Array<Portal>>([]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || !replacements) {
      setPortals([]);
      return;
    }

    const newPortals: Array<Portal> = [];

    replacements.forEach((replacement) => {
      const span = container.querySelector(`#${replacement.spanId}`);

      if (!span) {
        // eslint-disable-next-line no-console
        console.warn(
          `Target element not found for replacement: ${replacement.spanId}`,
        );
        return;
      }

      let element: JSX.Element;

      if (replacement.type === 'iframe') {
        const { hostName, providerName, aspectRatio } =
          extractConsentGuardProps(replacement.iframe);

        element = (
          <ConsentGuard
            hostName={hostName}
            providerName={providerName}
            aspectRatio={aspectRatio}
            isActive
          >
            {/* eslint-disable-next-line react/no-danger */}
            <div
              dangerouslySetInnerHTML={{ __html: replacement.iframe.outerHTML }}
            />
          </ConsentGuard>
        );
      } else if (replacement.type === 'image') {
        element = (
          <ImageWithPreview
            src={replacement.src}
            alt={replacement.alt}
            width={
              replacement.width ? parseInt(replacement.width, 10) : undefined
            }
            height={
              replacement.height ? parseInt(replacement.height, 10) : undefined
            }
            style={
              replacement.style ? parseCssString(replacement.style) : undefined
            }
            enablePreview={true}
          />
        );
      } else {
        // Skip unknown replacement types
        // eslint-disable-next-line no-console
        console.warn(
          `Unknown replacement type: ${(replacement as Replacement).type}`,
        );
        return;
      }

      newPortals.push({
        element,
        target: span,
        key: replacement.spanId,
      });
    });

    setPortals(newPortals);
  }, [replacements, containerRef]);

  return portals;
};
